import {
  Badge,
  Button,
  Card,
  CardHeader,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Link,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Tab,
  TabList,
  TabValue,
  TableColumnDefinition,
  Text,
  Title3,
  createTableColumn,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { ArrowClockwise20Regular } from "@fluentui/react-icons";
import { useQueryClient } from "@skanska/vsa-client-auth";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useHealthCheckHistory,
  useHealthCheckStatus,
  useRelinkToolFile,
  useStartHealthCheck,
} from "../../hooks/useTools";
import { JobStatus } from "../../models/Job";
import {
  DocumentHealthCheckItem,
  DocumentHealthCheckJobHistory,
} from "../../models/Tool";
import { ButtonWithLoading } from "../ButtonWithLoading";
import { LinkOrphanToDraftDialog } from "./LinkOrphanToDraftDialog";

const publishedToolsLibrary = "PublishedTools";
const publishedToolVersionsLibrary = "PublishedToolVersions";

const useStyles = makeStyles({
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  controls: {
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalS,
  },
  statusHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  jobInfo: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  jobInfoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  tabContent: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  noJobState: {
    textAlign: "center",
    padding: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground3,
  },
  historyContainer: {
    maxHeight: "500px",
    overflowY: "auto",
  },
  loadingState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXL,
  },
  errorState: {
    textAlign: "center",
    padding: tokens.spacingVerticalL,
  },
  viewingBanner: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  cellWrap: {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  orphanTitleCell: {
    minWidth: 0,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },
  orphanIdCell: {
    display: "block",
    maxWidth: "190px",
    overflowWrap: "anywhere",
  },
  orphanUrlCell: {
    display: "block",
    maxWidth: "760px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  orphanLibraryCell: {
    whiteSpace: "nowrap",
  },
  orphanActionCell: {
    whiteSpace: "nowrap",
  },
  clickableRow: {
    cursor: "pointer",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
});

function healthStatusColor(
  status: number
): "informative" | "success" | "danger" | "subtle" {
  if (status === JobStatus.Running) return "informative";
  if (status === JobStatus.Completed) return "success";
  if (status === JobStatus.Failed) return "danger";
  return "subtle";
}

function formatDuration(startTime: string, endTime?: string): string {
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();
  const ms = end.getTime() - start.getTime();
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function getRelinkSharePointLibrary(item: DocumentHealthCheckItem): string {
  return (
    item.sharePointLibrary ??
    (item.isCurrentVersion
      ? publishedToolsLibrary
      : publishedToolVersionsLibrary)
  );
}

export const DocumentHealthCheckPanel: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const queryClient = useQueryClient();

  const [selectedTab, setSelectedTab] = useState<TabValue>("results");
  const [pollTick, setPollTick] = useState(0);
  const [viewingJob, setViewingJob] =
    useState<DocumentHealthCheckJobHistory | null>(null);

  const [relinkItem, setRelinkItem] = useState<DocumentHealthCheckItem | null>(
    null
  );
  const [relinkDialogOpen, setRelinkDialogOpen] = useState(false);
  const [selectedOrphanId, setSelectedOrphanId] = useState("");
  const [relinkSuccess, setRelinkSuccess] = useState<string | null>(null);
  const [relinkError, setRelinkError] = useState<string | null>(null);

  const [linkOrphanItem, setLinkOrphanItem] =
    useState<DocumentHealthCheckItem | null>(null);
  const [linkOrphanDialogOpen, setLinkOrphanDialogOpen] = useState(false);

  const { data: latestJob, isLoading: latestJobLoading } =
    useHealthCheckStatus(pollTick);
  const {
    data: historyData,
    isLoading: historyLoading,
    error: historyError,
  } = useHealthCheckHistory();
  const { mutateAsync: startScan, isPending: scanIsPending } =
    useStartHealthCheck();
  const { mutateAsync: relinkFile, isPending: relinkIsPending } =
    useRelinkToolFile(relinkItem?.toolId ?? "");

  const refetch = useCallback(async () => {
    setPollTick((n) => n + 1);
    await queryClient.invalidateQueries({ queryKey: ["health-check-history"] });
  }, [queryClient]);

  useEffect(() => {
    if (latestJob?.status !== JobStatus.Running) return;
    const timer = setTimeout(() => setPollTick((n) => n + 1), 3000);
    return () => clearTimeout(timer);
  }, [latestJob]);

  useEffect(() => {
    if (latestJob?.status === JobStatus.Running) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect */
      setViewingJob(null);
    }
  }, [latestJob?.status]);

  const handleStartScan = async () => {
    setRelinkSuccess(null);
    setRelinkError(null);
    setViewingJob(null);
    await startScan();
    setTimeout(() => setPollTick((n) => n + 1), 500);
  };

  const handleViewHistory = (job: DocumentHealthCheckJobHistory) => {
    setViewingJob(job);
    setSelectedTab("results");
  };

  const handleOpenRelink = (item: DocumentHealthCheckItem) => {
    setRelinkItem(item);
    setSelectedOrphanId("");
    setRelinkSuccess(null);
    setRelinkError(null);
    setRelinkDialogOpen(true);
  };

  const handleOpenLinkToDraft = (item: DocumentHealthCheckItem) => {
    setLinkOrphanItem(item);
    setRelinkSuccess(null);
    setRelinkError(null);
    setLinkOrphanDialogOpen(true);
  };

  const handleRelink = async () => {
    if (!relinkItem || !selectedOrphanId.trim()) return;
    try {
      await relinkFile({
        newFileUniqueId: selectedOrphanId.trim(),
        versionId: relinkItem.versionId,
        sharePointLibrary: getRelinkSharePointLibrary(relinkItem),
      });
      setRelinkSuccess(relinkItem.title ?? relinkItem.toolId ?? "");
      setRelinkDialogOpen(false);
      setRelinkItem(null);
      await refetch();
    } catch {
      setRelinkError(t("documentManagement.health.relinkError"));
    }
  };

  const displayJob = viewingJob ?? latestJob;

  const missingFileItems = (displayJob?.healthCheckItems ?? []).filter(
    (i) => i.itemType === "ToolMissingFile"
  );
  const orphanItems = (displayJob?.healthCheckItems ?? []).filter(
    (i) => i.itemType === "OrphanFile"
  );

  const orphanItemsForDialog = relinkItem
    ? orphanItems.filter(
        (o) => (o.spLibrary ?? "") === getRelinkSharePointLibrary(relinkItem)
      )
    : [];

  const missingColumns: TableColumnDefinition<DocumentHealthCheckItem>[] = [
    createTableColumn<DocumentHealthCheckItem>({
      columnId: "title",
      renderHeaderCell: () => t("title"),
      renderCell: (item) => item.title ?? "",
    }),
    createTableColumn<DocumentHealthCheckItem>({
      columnId: "toolId",
      renderHeaderCell: () => t("documentManagement.health.toolId"),
      renderCell: (item) => item.toolId ?? "",
    }),
    createTableColumn<DocumentHealthCheckItem>({
      columnId: "versionId",
      renderHeaderCell: () => t("documentManagement.health.versionId"),
      renderCell: (item) => item.versionId ?? "",
    }),
    createTableColumn<DocumentHealthCheckItem>({
      columnId: "fileUniqueId",
      renderHeaderCell: () => t("documentManagement.health.fileUniqueId"),
      renderCell: (item) => (
        <span className={classes.cellWrap}>{item.fileUniqueId}</span>
      ),
    }),
    createTableColumn<DocumentHealthCheckItem>({
      columnId: "library",
      renderHeaderCell: () => t("documentManagement.health.library"),
      renderCell: (item) => item.sharePointLibrary ?? "",
    }),
    createTableColumn<DocumentHealthCheckItem>({
      columnId: "relink",
      renderHeaderCell: () => "",
      renderCell: (item) =>
        !viewingJob ? (
          <Button size="small" onClick={() => handleOpenRelink(item)}>
            {t("documentManagement.health.addFile")}
          </Button>
        ) : null,
    }),
  ];

  const orphanColumns: TableColumnDefinition<DocumentHealthCheckItem>[] = [
    createTableColumn<DocumentHealthCheckItem>({
      columnId: "spTitle",
      renderHeaderCell: () => t("title"),
      renderCell: (item) => (
        <span className={classes.orphanTitleCell}>{item.spTitle ?? ""}</span>
      ),
    }),
    createTableColumn<DocumentHealthCheckItem>({
      columnId: "spFileUniqueId",
      renderHeaderCell: () => t("documentManagement.health.fileUniqueId"),
      renderCell: (item) => (
        <span className={classes.orphanIdCell}>{item.spFileUniqueId}</span>
      ),
    }),
    createTableColumn<DocumentHealthCheckItem>({
      columnId: "spWebUrl",
      renderHeaderCell: () => t("documentManagement.health.url"),
      renderCell: (item) =>
        item.spWebUrl ? (
          <Link
            className={classes.orphanUrlCell}
            href={item.spWebUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={item.spWebUrl}
          >
            {item.spWebUrl}
          </Link>
        ) : null,
    }),
    createTableColumn<DocumentHealthCheckItem>({
      columnId: "spLibrary",
      renderHeaderCell: () => t("documentManagement.health.library"),
      renderCell: (item) => (
        <span className={classes.orphanLibraryCell}>{item.spLibrary ?? ""}</span>
      ),
    }),
    createTableColumn<DocumentHealthCheckItem>({
      columnId: "linkToDraft",
      renderHeaderCell: () => "",
      renderCell: (item) =>
        !viewingJob ? (
          <span className={classes.orphanActionCell}>
            <Button size="small" onClick={() => handleOpenLinkToDraft(item)}>
              {t("documentManagement.health.linkToDraft")}
            </Button>
          </span>
        ) : null,
    }),
  ];

  const historyColumns: TableColumnDefinition<DocumentHealthCheckJobHistory>[] =
    [
      createTableColumn<DocumentHealthCheckJobHistory>({
        columnId: "startTime",
        compare: (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
        renderHeaderCell: () =>
          t("documentManagement.health.history.startTime"),
        renderCell: (item) => (
          <Text>{new Date(item.startTime).toLocaleString()}</Text>
        ),
      }),
      createTableColumn<DocumentHealthCheckJobHistory>({
        columnId: "status",
        compare: (a, b) => a.status - b.status,
        renderHeaderCell: () => t("documentManagement.health.history.status"),
        renderCell: (item) => (
          <Badge appearance="filled" color={healthStatusColor(item.status)}>
            {t(
              `documentManagement.health.status.${JobStatus[item.status] ?? item.status}`
            )}
          </Badge>
        ),
      }),
      createTableColumn<DocumentHealthCheckJobHistory>({
        columnId: "duration",
        renderHeaderCell: () => t("documentHealthCheck.duration"),
        renderCell: (item) => (
          <Text>{formatDuration(item.startTime, item.endTime)}</Text>
        ),
      }),
      createTableColumn<DocumentHealthCheckJobHistory>({
        columnId: "missing",
        renderHeaderCell: () =>
          t("documentManagement.health.history.missingFiles"),
        renderCell: (item) =>
          String(
            (item.healthCheckItems ?? []).filter(
              (i) => i.itemType === "ToolMissingFile"
            ).length
          ),
      }),
      createTableColumn<DocumentHealthCheckJobHistory>({
        columnId: "orphans",
        renderHeaderCell: () =>
          t("documentManagement.health.history.orphanFiles"),
        renderCell: (item) =>
          String(
            (item.healthCheckItems ?? []).filter(
              (i) => i.itemType === "OrphanFile"
            ).length
          ),
      }),
      createTableColumn<DocumentHealthCheckJobHistory>({
        columnId: "triggeredBy",
        renderHeaderCell: () =>
          t("documentManagement.health.history.triggeredBy"),
        renderCell: (item) =>
          item.triggeredByUserId === "cron"
            ? t("documentManagement.health.history.triggeredByCron")
            : (item.triggeredByUserId ?? "-"),
      }),
    ];

  return (
    <div className={classes.panel}>
      {/* Controls */}
      <div className={classes.controls}>
        <Button
          icon={<ArrowClockwise20Regular />}
          appearance="subtle"
          onClick={refetch}
        >
          {t("documentHealthCheck.refresh")}
        </Button>
        <ButtonWithLoading
          text={t("documentManagement.health.startScan")}
          isLoading={scanIsPending || latestJob?.status === JobStatus.Running}
          onClick={handleStartScan}
        />
      </div>

      {/* Status card */}
      <Card>
        <CardHeader
          className={classes.statusHeader}
          header={
            <div className={classes.statusHeader}>
              <Text size={500} weight="semibold">
                {t("documentHealthCheck.latestJobStatus")}
              </Text>
              {latestJobLoading && !latestJob && <Spinner size="tiny" />}
              {latestJob && (
                <Badge
                  appearance="filled"
                  color={healthStatusColor(latestJob.status)}
                >
                  {t(
                    `documentManagement.health.status.${JobStatus[latestJob.status] ?? latestJob.status}`
                  )}
                </Badge>
              )}
            </div>
          }
        />
        {!latestJob && !latestJobLoading ? (
          <div className={classes.noJobState}>
            <Text>{t("documentHealthCheck.noJobYet")}</Text>
          </div>
        ) : !latestJob && latestJobLoading ? (
          <div className={classes.loadingState}>
            <Spinner size="tiny" />
          </div>
        ) : latestJob ? (
          <div className={classes.jobInfo}>
            <div className={classes.jobInfoRow}>
              <Text>{t("documentHealthCheck.startTime")}:</Text>
              <Text>{new Date(latestJob.startTime).toLocaleString()}</Text>
            </div>
            {latestJob.endTime && (
              <div className={classes.jobInfoRow}>
                <Text>{t("documentHealthCheck.endTime")}:</Text>
                <Text>{new Date(latestJob.endTime).toLocaleString()}</Text>
              </div>
            )}
            <div className={classes.jobInfoRow}>
              <Text>{t("documentHealthCheck.duration")}:</Text>
              <Text>
                {formatDuration(latestJob.startTime, latestJob.endTime)}
              </Text>
            </div>
            {latestJob.status === JobStatus.Running && (
              <div className={classes.jobInfoRow}>
                <Text>{t("documentHealthCheck.progress")}:</Text>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Spinner size="tiny" />
                  <Text>
                    {latestJob.progress?.currentStep ??
                      t("documentManagement.health.scanning")}
                  </Text>
                </div>
              </div>
            )}
            {latestJob.status === JobStatus.Completed && (
              <div className={classes.jobInfoRow}>
                <Text>{t("documentHealthCheck.findings")}:</Text>
                <Text>
                  {t("documentHealthCheck.findingsSummary", {
                    missing: missingFileItems.length,
                    orphans: orphanItems.length,
                  })}
                </Text>
              </div>
            )}
          </div>
        ) : null}
      </Card>

      {/* Messages */}
      {relinkSuccess && (
        <MessageBar intent="success">
          <MessageBarBody>
            {t("documentManagement.health.relinkSuccess", {
              title: relinkSuccess,
            })}
          </MessageBarBody>
        </MessageBar>
      )}
      {relinkError && (
        <MessageBar intent="error">
          <MessageBarBody>{relinkError}</MessageBarBody>
        </MessageBar>
      )}

      {/* Tabs */}
      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_, data) => setSelectedTab(data.value)}
      >
        <Tab value="results">{t("documentHealthCheck.resultsTab")}</Tab>
        <Tab value="history">{t("documentHealthCheck.historyTab")}</Tab>
      </TabList>

      <div className={classes.tabContent}>
        {/* Results tab */}
        {selectedTab === "results" && (
          <>
            {viewingJob && (
              <div className={classes.viewingBanner}>
                <MessageBar intent="info">
                  <MessageBarBody>
                    {t("documentHealthCheck.viewingHistoricalRun", {
                      date: new Date(viewingJob.startTime).toLocaleString(),
                    })}
                    &nbsp;
                    <Button
                      size="small"
                      appearance="subtle"
                      onClick={() => setViewingJob(null)}
                    >
                      {t("documentHealthCheck.showLatest")}
                    </Button>
                  </MessageBarBody>
                </MessageBar>
              </div>
            )}

            {!displayJob && latestJobLoading && (
              <div className={classes.loadingState}>
                <Spinner size="tiny" />
              </div>
            )}

            {!displayJob && !latestJobLoading && (
              <div className={classes.noJobState}>
                <Text>{t("documentManagement.health.noResults")}</Text>
              </div>
            )}

            {displayJob &&
              displayJob.status !== JobStatus.Completed &&
              !viewingJob && (
                <div className={classes.noJobState}>
                  <Text>{t("documentManagement.health.noResults")}</Text>
                </div>
              )}

            {displayJob &&
              (displayJob.status === JobStatus.Completed || viewingJob) && (
                <>
                  <Title3>
                    {t("documentManagement.health.missingFileTitle")}
                  </Title3>
                  {missingFileItems.length === 0 ? (
                    <MessageBar intent="success">
                      <MessageBarBody>
                        {t("documentManagement.health.noMissingFiles")}
                      </MessageBarBody>
                    </MessageBar>
                  ) : (
                    <DataGrid
                      items={missingFileItems}
                      columns={missingColumns}
                      getRowId={(item) => item.versionId ?? item.toolId ?? ""}
                      resizableColumns
                      columnSizingOptions={{
                        title: { idealWidth: 180, minWidth: 120 },
                        toolId: { idealWidth: 140, minWidth: 100 },
                        fileUniqueId: { idealWidth: 260, minWidth: 160 },
                        library: { idealWidth: 160, minWidth: 100 },
                        relink: { idealWidth: 120, minWidth: 90 },
                      }}
                    >
                      <DataGridHeader>
                        <DataGridRow>
                          {({ renderHeaderCell }) => (
                            <DataGridHeaderCell>
                              {renderHeaderCell()}
                            </DataGridHeaderCell>
                          )}
                        </DataGridRow>
                      </DataGridHeader>
                      <DataGridBody<DocumentHealthCheckItem>>
                        {({ item, rowId }) => (
                          <DataGridRow<DocumentHealthCheckItem> key={rowId}>
                            {({ renderCell }) => (
                              <DataGridCell>{renderCell(item)}</DataGridCell>
                            )}
                          </DataGridRow>
                        )}
                      </DataGridBody>
                    </DataGrid>
                  )}

                  <Title3>
                    {t("documentManagement.health.orphanFileTitle")}
                  </Title3>
                  {orphanItems.length === 0 ? (
                    <MessageBar intent="success">
                      <MessageBarBody>
                        {t("documentManagement.health.noOrphanFiles")}
                      </MessageBarBody>
                    </MessageBar>
                  ) : (
                    <DataGrid
                      items={orphanItems}
                      columns={orphanColumns}
                      getRowId={(item) =>
                        item.spFileUniqueId ?? item.spTitle ?? ""
                      }
                      resizableColumns
                      columnSizingOptions={{
                        spTitle: { idealWidth: 220, minWidth: 160 },
                        spFileUniqueId: { idealWidth: 190, minWidth: 150 },
                        spWebUrl: { idealWidth: 760, minWidth: 320 },
                        spLibrary: { idealWidth: 170, minWidth: 150 },
                        linkToDraft: { idealWidth: 150, minWidth: 150 },
                      }}
                    >
                      <DataGridHeader>
                        <DataGridRow>
                          {({ renderHeaderCell }) => (
                            <DataGridHeaderCell>
                              {renderHeaderCell()}
                            </DataGridHeaderCell>
                          )}
                        </DataGridRow>
                      </DataGridHeader>
                      <DataGridBody<DocumentHealthCheckItem>>
                        {({ item, rowId }) => (
                          <DataGridRow<DocumentHealthCheckItem> key={rowId}>
                            {({ renderCell }) => (
                              <DataGridCell>{renderCell(item)}</DataGridCell>
                            )}
                          </DataGridRow>
                        )}
                      </DataGridBody>
                    </DataGrid>
                  )}
                </>
              )}
          </>
        )}

        {/* History tab */}
        {selectedTab === "history" && (
          <Card>
            <Text weight="semibold">
              {t("documentManagement.health.history.title")}
            </Text>
            {historyLoading ? (
              <div className={classes.loadingState}>
                <Spinner label={t("loading")} />
              </div>
            ) : historyError ? (
              <div className={classes.errorState}>
                <Text>{t("documentHealthCheck.historyLoadError")}</Text>
              </div>
            ) : !historyData?.length ? (
              <div className={classes.noJobState}>
                <Text>{t("documentManagement.health.history.noHistory")}</Text>
              </div>
            ) : (
              <div className={classes.historyContainer}>
                <DataGrid
                  items={historyData}
                  columns={historyColumns}
                  sortable
                  getRowId={(item) => item.id}
                  resizableColumns
                  columnSizingOptions={{
                    startTime: { idealWidth: 175, minWidth: 130 },
                    status: { idealWidth: 110, minWidth: 90 },
                    duration: { idealWidth: 110, minWidth: 90 },
                    missing: { idealWidth: 200, minWidth: 100 },
                    orphans: { idealWidth: 200, minWidth: 100 },
                    triggeredBy: { idealWidth: 200, minWidth: 120 },
                  }}
                >
                  <DataGridHeader>
                    <DataGridRow>
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell>
                          {renderHeaderCell()}
                        </DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody<DocumentHealthCheckJobHistory>>
                    {({ item, rowId }) => (
                      <DataGridRow<DocumentHealthCheckJobHistory>
                        key={rowId}
                        className={classes.clickableRow}
                        onClick={() => handleViewHistory(item)}
                      >
                        {({ renderCell }) => (
                          <DataGridCell>{renderCell(item)}</DataGridCell>
                        )}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Relink dialog */}
      <Dialog
        open={relinkDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setRelinkDialogOpen(false);
            setRelinkItem(null);
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t("documentManagement.health.relinkDialogTitle")}
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <Text>
                {t("documentManagement.health.relinkDialogMessage", {
                  title: relinkItem?.title ?? relinkItem?.toolId ?? "",
                })}
              </Text>
              {orphanItemsForDialog.length === 0 ? (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    {t("documentManagement.health.noOrphanFilesForRelink")}
                  </MessageBarBody>
                </MessageBar>
              ) : (
                <Field
                  label={t("documentManagement.health.newFileUniqueIdLabel")}
                >
                  <Dropdown
                    value={
                      orphanItemsForDialog.find(
                        (i) => i.spFileUniqueId === selectedOrphanId
                      )?.spTitle ?? ""
                    }
                    selectedOptions={selectedOrphanId ? [selectedOrphanId] : []}
                    onOptionSelect={(_, data) =>
                      setSelectedOrphanId(data.optionValue ?? "")
                    }
                    placeholder={t(
                      "documentManagement.health.newFileUniqueIdPlaceholder"
                    )}
                  >
                    {orphanItemsForDialog.map((item) => (
                      <Option
                        key={item.spFileUniqueId ?? item.spTitle ?? ""}
                        value={item.spFileUniqueId ?? ""}
                        text={item.spTitle ?? item.spFileUniqueId ?? ""}
                      >
                        {item.spTitle ?? item.spFileUniqueId ?? ""}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setRelinkDialogOpen(false);
                  setRelinkItem(null);
                }}
              >
                {t("cancel")}
              </Button>
              <ButtonWithLoading
                text={t("documentManagement.health.confirmRelink")}
                isLoading={relinkIsPending}
                onClick={handleRelink}
              />
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Link orphan to draft dialog */}
      <LinkOrphanToDraftDialog
        orphanItem={linkOrphanItem}
        open={linkOrphanDialogOpen}
        onClose={() => {
          setLinkOrphanDialogOpen(false);
          setLinkOrphanItem(null);
        }}
        onSuccess={async (message) => {
          setRelinkSuccess(message);
          await refetch();
        }}
      />
    </div>
  );
};
